import { Document, Page, Text, View, StyleSheet, pdf } from "@react-pdf/renderer";

const styles = StyleSheet.create({
  page: { padding: 40, fontSize: 10, color: "#1f2937", lineHeight: 1.5 },
  header: { backgroundColor: "#1E3A8A", color: "#FFFFFF", padding: 20, marginHorizontal: -40, marginTop: -40, marginBottom: 24 },
  brand: { fontSize: 14, fontWeight: 700, marginBottom: 2 },
  brandTag: { fontSize: 9, color: "#A7F3D0", fontStyle: "italic" },
  title: { fontSize: 20, fontWeight: 700, marginTop: 16, color: "#FFFFFF" },
  subtitle: { fontSize: 10, color: "#DBEAFE", marginTop: 4 },
  section: { marginTop: 18 },
  sectionTitle: { fontSize: 12, fontWeight: 700, color: "#1E3A8A", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 },
  scoreBox: { backgroundColor: "#F0FDF4", borderColor: "#10B981", borderWidth: 1, borderStyle: "solid", borderRadius: 8, padding: 18, flexDirection: "row", alignItems: "center", justifyContent: "space-between" },
  scoreLeft: { flexDirection: "column" },
  scoreLabel: { fontSize: 9, color: "#047857", textTransform: "uppercase", letterSpacing: 0.5 },
  scoreNumber: { fontSize: 36, fontWeight: 700, color: "#065F46", marginTop: 4 },
  scoreNote: { fontSize: 9, color: "#374151", maxWidth: 240, textAlign: "right" },
  recCard: { borderColor: "#E5E7EB", borderWidth: 1, borderStyle: "solid", borderRadius: 6, padding: 12, marginBottom: 8 },
  recHeader: { flexDirection: "row", justifyContent: "space-between", alignItems: "center" },
  recName: { fontSize: 11, fontWeight: 700, color: "#111827" },
  pill: { fontSize: 8, fontWeight: 700, padding: 4, borderRadius: 4, color: "#FFFFFF" },
  pillHigh: { backgroundColor: "#dc2626" },
  pillMedium: { backgroundColor: "#d97706" },
  pillLow: { backgroundColor: "#6b7280" },
  cover: { fontSize: 9, color: "#047857", marginTop: 4, fontWeight: 600 },
  rationale: { fontSize: 9, color: "#374151", marginTop: 4 },
  profileRow: { flexDirection: "row", flexWrap: "wrap", gap: 8 },
  profileCell: { width: "48%", marginBottom: 6 },
  pLabel: { fontSize: 8, color: "#6b7280", textTransform: "uppercase" },
  pValue: { fontSize: 10, color: "#111827", fontWeight: 600, marginTop: 2 },
  footer: { position: "absolute", bottom: 24, left: 40, right: 40, fontSize: 8, color: "#6b7280", textAlign: "center", borderTopWidth: 1, borderTopColor: "#E5E7EB", borderTopStyle: "solid", paddingTop: 8 },
  cta: { backgroundColor: "#1E3A8A", color: "#FFFFFF", padding: 14, borderRadius: 6, marginTop: 20 },
  ctaTitle: { fontSize: 12, fontWeight: 700, color: "#FFFFFF" },
  ctaText: { fontSize: 9, color: "#DBEAFE", marginTop: 4 },
});

const pillStyle = (p) => [styles.pill, p === "high" ? styles.pillHigh : p === "medium" ? styles.pillMedium : styles.pillLow];

function inr(n) {
  if (!n && n !== 0) return "—";
  return "Rs. " + Math.round(n).toLocaleString("en-IN");
}

export function AssessmentReport({ data, result }) {
  const scoreNote =
    result.health_score >= 75 ? "Excellent foundation. A few fine-tunes will optimise it." :
    result.health_score >= 50 ? "Decent start — but meaningful gaps remain." :
    "Several urgent gaps. Let's prioritise the high-impact ones.";

  const priorityRank = { high: 0, medium: 1, low: 2 };
  const sorted = [...result.recommendations].sort((a, b) => priorityRank[a.priority] - priorityRank[b.priority]);

  return (
    <Document title="Samota Insurance — Personalised Financial Health Report" author="Samota Insurance n Investments">
      <Page size="A4" style={styles.page}>
        <View style={styles.header} fixed>
          <Text style={styles.brand}>SAMOTA INSURANCE n INVESTMENTS</Text>
          <Text style={styles.brandTag}>We insure your happiness.</Text>
          <Text style={styles.title}>Personalised Financial Health Report</Text>
          <Text style={styles.subtitle}>Prepared for {data.name || "you"} · {new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Your Profile</Text>
          <View style={styles.profileRow}>
            <View style={styles.profileCell}><Text style={styles.pLabel}>Age</Text><Text style={styles.pValue}>{data.age} yrs</Text></View>
            <View style={styles.profileCell}><Text style={styles.pLabel}>Annual Income</Text><Text style={styles.pValue}>{inr(data.annual_income)}</Text></View>
            <View style={styles.profileCell}><Text style={styles.pLabel}>Dependents</Text><Text style={styles.pValue}>{data.dependents}</Text></View>
            <View style={styles.profileCell}><Text style={styles.pLabel}>Monthly Savings</Text><Text style={styles.pValue}>{inr(data.monthly_savings)}</Text></View>
            <View style={styles.profileCell}><Text style={styles.pLabel}>Health Cover</Text><Text style={styles.pValue}>{data.has_health_cover ? "Yes" : "No"}</Text></View>
            <View style={styles.profileCell}><Text style={styles.pLabel}>Term Cover</Text><Text style={styles.pValue}>{data.has_term_cover ? "Yes" : "No"}</Text></View>
            <View style={styles.profileCell}><Text style={styles.pLabel}>Marital Status</Text><Text style={styles.pValue}>{data.married ? "Married" : "Single"}</Text></View>
            <View style={styles.profileCell}><Text style={styles.pLabel}>Business Owner</Text><Text style={styles.pValue}>{data.business_owner ? "Yes" : "No"}</Text></View>
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Financial Health Score</Text>
          <View style={styles.scoreBox}>
            <View style={styles.scoreLeft}>
              <Text style={styles.scoreLabel}>Your score</Text>
              <Text style={styles.scoreNumber}>{result.health_score} / 100</Text>
            </View>
            <Text style={styles.scoreNote}>{scoreNote}</Text>
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Priority Recommendations ({sorted.length})</Text>
          {sorted.map((r, i) => (
            <View key={i} style={styles.recCard} wrap={false}>
              <View style={styles.recHeader}>
                <Text style={styles.recName}>{i + 1}. {r.product}</Text>
                <Text style={pillStyle(r.priority)}>{r.priority.toUpperCase()}</Text>
              </View>
              {r.suggested_cover ? <Text style={styles.cover}>Suggested: {r.suggested_cover}</Text> : null}
              <Text style={styles.rationale}>{r.rationale}</Text>
            </View>
          ))}
        </View>

        <View style={styles.cta} wrap={false}>
          <Text style={styles.ctaTitle}>Next step — speak to a CFP-led advisor</Text>
          <Text style={styles.ctaText}>Vinay Samota, CFP - +91 98212 37924 - office@samotainsurance.com</Text>
          <Text style={styles.ctaText}>Office No. 103, Sagar Heights, Near Jawahar Library, Station Road, Kalwa West, Thane 400605</Text>
        </View>

        <Text style={styles.footer} fixed>
          This report is for educational purposes. Insurance is the subject matter of solicitation. Insurance is subject to policy terms and conditions. Mutual Fund investments are subject to market risks. Read all scheme-related documents carefully before investing.
        </Text>
      </Page>
    </Document>
  );
}

export async function downloadAssessmentReport(data, result) {
  const blob = await pdf(<AssessmentReport data={data} result={result} />).toBlob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `Samota-Financial-Health-Report-${(data.name || "Report").replace(/\s+/g, "-")}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
